// React Query
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

// Utils
import queryString from 'api/utils/queryStringBuilder';
import { getRequest as get } from '../utils/HttpMethods';

// Interface
import { Remittance, isRemittance } from 'interfaces';

// Sentry
import {
  updateEntity,
  useGetQuery,
  useListQuery,
  useMutationQuery,
} from '../utils/queries';

export const remittanceQueryKeys = {
  all: ['remittances'],
  listRemittanceBySite: (siteID: string | string[]) => [
    ...remittanceQueryKeys.all,
    siteID,
  ],
  get: (id: string) => [...remittanceQueryKeys.all, id],
};

/**
 * @description Updates a remittance entity.
 * @param {Remittance} remittance - The remittance entity to update.
 * @returns {Promise<Remittance | null>} The updated remittance entity or null if the update fails.
 */
const updateRemittance = async (
  remittance: Remittance
): Promise<Remittance | null> => {
  return updateEntity<Remittance>({
    entity: remittance,
    functionName: 'updateRemittance',
    isEntity: isRemittance,
    path: '/resource/remittance',
  });
};

/**
 * @description Fetches a remittance entity by its ID.
 * @param {string} id - The ID of the remittance to fetch.
 * @returns {Promise<Remittance | null>} The fetched remittance entity or null if not found.
 */
const getRemittance = async (id: string): Promise<Remittance | null> =>
  await get<Remittance>(
    'QBilling',
    `/resource/remittance${queryString({ remittanceID: id })}`
  );

/**
 * @description Hook to fetch a remittance entity by its ID.
 * @param {Object} params - The parameters for the hook.
 * @param {string} params.id - The ID of the remittance to fetch.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<Remittance | null, Error>} The query result containing the remittance entity or an error.
 */
export const useGetRemittance = ({
  id,
  displayNotification = false,
  enabled = false,
}: {
  id: string;
  displayNotification?: boolean;
  enabled?: boolean;
}): UseQueryResult<Remittance | null, Error> => {
  return useGetQuery<Remittance>({
    queryKey: remittanceQueryKeys.get(id),
    fetchFunction: getRemittance,
    functionName: 'getRemittance',
    params: id,
    enabled: !!id && enabled,
    isEntity: isRemittance,
    displayNotification,
  });
};

/**
 * @description Lists remittances by site ID.
 * @param {string} siteID - The ID of the site to list remittances for.
 * @returns {Promise<Remittance[]>} The list of remittances for the specified site.
 */
const listRemittanceBySite = async ({
  siteID,
}: {
  siteID: string | string[];
}): Promise<Remittance[]> =>
  await get<Remittance[]>(
    'QBilling',
    `/resource/remittance/site${queryString({ siteID })}`
  );

/**
 * @description Hook to list remittances by site ID.
 * @param {Object} params - The parameters for the hook.
 * @param {string} params.siteID - The ID of the site to list remittances for.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<Remittance[], Error>} The query result containing the list of remittances or an error.
 */
export const useListRemittanceBySite = ({
  siteID,
  displayNotification = false,
}: {
  siteID: string | string[];
  displayNotification?: boolean;
}): UseQueryResult<Remittance[], Error> => {
  return useListQuery<Remittance>({
    queryKey: remittanceQueryKeys.listRemittanceBySite(siteID),
    fetchFunction: listRemittanceBySite,
    functionName: 'listRemittanceBySite',
    isEntity: isRemittance,
    params: { siteID },
    enabled: Array.isArray(siteID) ? siteID.length > 0 : !!siteID,
    displayNotification: displayNotification,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

/**
 * @description Hook to update a remittance entity.
 * @returns {UseMutationResult<Remittance | null, Error>} The mutation result containing the updated remittance entity or an error.
 */
export const useUpdateRemittance = (): UseMutationResult<
  Remittance | null,
  Error
> => {
  return useMutationQuery<Remittance>({
    mutationFn: updateRemittance,
    successMessage: 'Remittance Updated',
    errorMessage: 'Remittance Update Failed',
    queryKey: remittanceQueryKeys.all,
  });
};
